<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-2xl font-bold text-gray-800 flex items-center gap-2">
          Data Duplication
          <BaseInfoIcon :text="ERKLAERUNG.datenDuplikation" label="Data Duplication" />
        </h2>
        <p class="mt-1 text-sm text-gray-600">
          Duplicate data from one Micro-Credential to others
        </p>
      </div>
    </div>

    <!-- Info Box -->
    <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-blue-800">
            About Data Duplication
          </h3>
          <div class="mt-2 text-sm text-blue-700">
            <p>This feature allows you to copy data from one Micro-Credential to one or more target Micro-Credentials. Currently supported:</p>
            <ul class="list-disc list-inside mt-1 ml-2">
              <li>Criteria (countable, boolean, text)</li>
              <li>Roles</li>
            </ul>
            <p class="mt-2">Duplicates will be skipped if an item with the same name already exists in the target credential.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
      {{ errorMessage }}
    </div>

    <!-- Success Message -->
    <div v-if="successMessage" class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
      {{ successMessage }}
    </div>

    <!-- Duplication Form -->
    <div class="bg-white rounded-lg shadow p-6 space-y-6">
      <!-- Data Type Selection -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Data Type to Duplicate *
        </label>
        <select
          v-model="selectedDataType"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          @change="onDataTypeChange"
        >
          <option value="">-- Select Data Type --</option>
          <option value="criteria">Criteria</option>
          <option value="roles">Roles</option>
          <option value="teams">Teams</option>
          <!-- Future data types can be added here -->
          <option value="templates" disabled>Team Templates (Coming Soon)</option>
        </select>
      </div>

      <div v-if="selectedDataType === 'criteria'" class="space-y-6">
        <!-- Source Credential -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Source Micro-Credential *
          </label>
          <select
            v-model="sourceCredentialId"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            @change="onSourceChange"
          >
            <option :value="null">-- Select Source Credential --</option>
            <option
              v-for="cred in credentials"
              :key="cred.id"
              :value="cred.id"
            >
              {{ cred.name }}
            </option>
          </select>
        </div>

        <!-- Criteria Selection -->
        <div v-if="sourceCredentialId !== null">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Criteria to Duplicate
          </label>
          <div v-if="loadingCriteria" class="text-sm text-gray-600">
            Loading criteria...
          </div>
          <div v-else-if="availableCriteria.length === 0" class="text-sm text-gray-600">
            No criteria found in the selected credential.
          </div>
          <div v-else class="space-y-2">
            <div class="flex items-center mb-2">
              <input
                type="checkbox"
                id="selectAllCriteria"
                v-model="selectAllCriteria"
                @change="toggleSelectAll"
                class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label for="selectAllCriteria" class="ml-2 text-sm font-medium text-gray-700">
                Select All ({{ availableCriteria.length }} criteria)
              </label>
            </div>
            <div class="border border-gray-300 rounded-md max-h-60 overflow-y-auto p-3 space-y-2">
              <div
                v-for="criterion in availableCriteria"
                :key="criterion.id"
                class="flex items-start"
              >
                <input
                  type="checkbox"
                  :id="`criterion-${criterion.id}`"
                  :value="criterion.id"
                  v-model="selectedCriteriaIds"
                  class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mt-0.5"
                />
                <label
                  :for="`criterion-${criterion.id}`"
                  class="ml-2 text-sm text-gray-700 flex-1"
                >
                  <span class="font-medium">{{ criterion.name }}</span>
                  <span class="text-gray-500 ml-2">({{ criterion.type }})</span>
                  <span v-if="criterion.description" class="block text-xs text-gray-500 mt-0.5">
                    {{ criterion.description }}
                  </span>
                </label>
              </div>
            </div>
            <p class="text-xs text-gray-500 mt-1">
              Leave unchecked to duplicate all criteria
            </p>
          </div>
        </div>

        <!-- Target Credentials -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Target Micro-Credentials *
          </label>
          <div v-if="targetCredentials.length === 0" class="text-sm text-gray-600">
            Please select a source credential first.
          </div>
          <div v-else class="space-y-2">
            <div class="border border-gray-300 rounded-md max-h-60 overflow-y-auto p-3 space-y-2">
              <div
                v-for="cred in targetCredentials"
                :key="cred.id"
                class="flex items-center"
              >
                <input
                  type="checkbox"
                  :id="`target-${cred.id}`"
                  :value="cred.id"
                  v-model="selectedTargetIds"
                  class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label
                  :for="`target-${cred.id}`"
                  class="ml-2 text-sm text-gray-700 flex-1"
                >
                  {{ cred.name }}
                  <span v-if="cred.description" class="text-gray-500 text-xs block">
                    {{ cred.description }}
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Duplicate Button -->
        <div class="flex justify-end">
          <BaseButton
            @click="duplicateCriteriaData"
            :disabled="!canDuplicate || duplicating"
            class="px-6 py-2"
          >
            {{ duplicating ? 'Duplicating...' : 'Duplicate Criteria' }}
          </BaseButton>
        </div>
      </div>

      <!-- ROLES SECTION -->
      <div v-if="selectedDataType === 'roles'" class="space-y-6">
        <!-- Source Credential -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Source Micro-Credential *
          </label>
          <select
            v-model="sourceCredentialId"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            @change="onSourceChange"
          >
            <option :value="null">-- Select Source Credential --</option>
            <option
              v-for="cred in credentials"
              :key="cred.id"
              :value="cred.id"
            >
              {{ cred.name }}
            </option>
          </select>
        </div>

        <!-- Roles Selection -->
        <div v-if="sourceCredentialId !== null">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Roles to Duplicate
          </label>
          <div v-if="loadingRoles" class="text-sm text-gray-600">
            Loading roles...
          </div>
          <div v-else-if="availableRoles.length === 0" class="text-sm text-gray-600">
            No roles found in the selected credential.
          </div>
          <div v-else class="space-y-2">
            <div class="flex items-center mb-2">
              <input
                type="checkbox"
                id="selectAllRoles"
                v-model="selectAllRoles"
                @change="toggleSelectAllRoles"
                class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label for="selectAllRoles" class="ml-2 text-sm font-medium text-gray-700">
                Select All ({{ availableRoles.length }} roles)
              </label>
            </div>
            <div class="border border-gray-300 rounded-md max-h-60 overflow-y-auto p-3 space-y-2">
              <div
                v-for="role in availableRoles"
                :key="role.id"
                class="flex items-start"
              >
                <input
                  type="checkbox"
                  :id="`role-${role.id}`"
                  :value="role.id"
                  v-model="selectedRoleIds"
                  class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mt-0.5"
                />
                <label
                  :for="`role-${role.id}`"
                  class="ml-2 text-sm text-gray-700 flex-1"
                >
                  <span class="font-medium">{{ role.name }}</span>
                  <span v-if="role.description" class="block text-xs text-gray-500 mt-0.5">
                    {{ role.description }}
                  </span>
                </label>
              </div>
            </div>
            <p class="text-xs text-gray-500 mt-1">
              Leave unchecked to duplicate all roles
            </p>
          </div>
        </div>

        <!-- Target Credentials -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Target Micro-Credentials *
          </label>
          <div v-if="targetCredentials.length === 0" class="text-sm text-gray-600">
            Please select a source credential first.
          </div>
          <div v-else class="space-y-2">
            <div class="border border-gray-300 rounded-md max-h-60 overflow-y-auto p-3 space-y-2">
              <div
                v-for="cred in targetCredentials"
                :key="cred.id"
                class="flex items-center"
              >
                <input
                  type="checkbox"
                  :id="`target-role-${cred.id}`"
                  :value="cred.id"
                  v-model="selectedTargetIds"
                  class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label
                  :for="`target-role-${cred.id}`"
                  class="ml-2 text-sm text-gray-700 flex-1"
                >
                  {{ cred.name }}
                  <span v-if="cred.description" class="text-gray-500 text-xs block">
                    {{ cred.description }}
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Duplicate Button -->
        <div class="flex justify-end">
          <BaseButton
            @click="duplicateRolesData"
            :disabled="!canDuplicateRoles || duplicating"
            class="px-6 py-2"
          >
            {{ duplicating ? 'Duplicating...' : 'Duplicate Roles' }}
          </BaseButton>
        </div>
      </div>

      <!-- TEAMS SECTION -->
      <div v-if="selectedDataType === 'teams'" class="space-y-6">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Source Micro-Credential *
          </label>
          <select
            v-model="sourceCredentialId"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            @change="onSourceChange"
          >
            <option :value="null">-- Select Source Credential --</option>
            <option v-for="cred in credentials" :key="cred.id" :value="cred.id">
              {{ cred.name }}
            </option>
          </select>
        </div>

        <div v-if="sourceCredentialId !== null">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Teams to Duplicate
          </label>
          <div v-if="loadingTeams" class="text-sm text-gray-600">Loading teams...</div>
          <div v-else-if="availableTeams.length === 0" class="text-sm text-gray-600">
            No teams found in the selected credential.
          </div>
          <div v-else class="space-y-2">
            <div class="flex items-center mb-2">
              <input
                id="selectAllTeams"
                v-model="selectAllTeams"
                type="checkbox"
                @change="toggleSelectAllTeams"
                class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label for="selectAllTeams" class="ml-2 text-sm font-medium text-gray-700">
                Select All ({{ availableTeams.length }} teams)
              </label>
            </div>
            <div class="border border-gray-300 rounded-md max-h-60 overflow-y-auto p-3 space-y-2">
              <div v-for="team in availableTeams" :key="team.id" class="flex items-center">
                <input
                  :id="`team-${team.id}`"
                  v-model="selectedTeamIds"
                  type="checkbox"
                  :value="team.id"
                  class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label :for="`team-${team.id}`" class="ml-2 text-sm text-gray-700">
                  {{ team.name }}
                </label>
              </div>
            </div>
            <p class="text-xs text-gray-500 mt-1">Leave unchecked to duplicate all teams</p>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Target Micro-Credentials *
          </label>
          <div v-if="targetCredentials.length === 0" class="text-sm text-gray-600">
            Please select a source credential first.
          </div>
          <div v-else class="border border-gray-300 rounded-md max-h-60 overflow-y-auto p-3 space-y-2">
            <div v-for="cred in targetCredentials" :key="cred.id" class="flex items-center">
              <input
                :id="`target-team-${cred.id}`"
                v-model="selectedTargetIds"
                type="checkbox"
                :value="cred.id"
                class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label :for="`target-team-${cred.id}`" class="ml-2 text-sm text-gray-700">
                {{ cred.name }}
              </label>
            </div>
          </div>
        </div>

        <div class="flex justify-end">
          <BaseButton
            @click="duplicateTeamsData"
            :disabled="!canDuplicateTeams || duplicating"
            class="px-6 py-2"
          >
            {{ duplicating ? 'Duplicating...' : 'Duplicate Teams' }}
          </BaseButton>
        </div>
      </div>
    </div>

    <!-- Results Section -->
    <div v-if="duplicationResults.length > 0" class="bg-white rounded-lg shadow p-6">
      <h3 class="text-lg font-semibold text-gray-800 mb-4">Duplication Results</h3>
      <div class="space-y-4">
        <div
          v-for="(result, index) in duplicationResults"
          :key="index"
          class="border border-gray-200 rounded-lg p-4"
        >
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <h4 class="font-medium text-gray-900">{{ result.target_credential_name }}</h4>
              <div class="mt-2 text-sm space-y-1">
                <p class="text-green-600">
                  ✓ Duplicated: {{ result.duplicated_count }} item(s)
                </p>
                <p v-if="result.skipped_count > 0" class="text-yellow-600">
                  ⊘ Skipped (already exists): {{ result.skipped_count }} item(s)
                </p>
                <div v-if="result.errors.length > 0" class="text-red-600 space-y-1">
                  <p class="font-medium">Errors:</p>
                  <ul class="list-disc list-inside ml-2">
                    <li v-for="(error, errIndex) in result.errors" :key="errIndex">
                      {{ error }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="ml-4">
              <span
                :class="[
                  'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                  result.errors.length > 0
                    ? 'bg-red-100 text-red-800'
                    : result.duplicated_count > 0
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                ]"
              >
                {{ result.errors.length > 0 ? 'With Errors' : result.duplicated_count > 0 ? 'Success' : 'No Changes' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from '@/BaseComponents/BaseButton.vue';
import BaseInfoIcon from '@/BaseComponents/BaseInfoIcon.vue';
import { ERKLAERUNG } from '@/async-sessions/erklaerungen';
import { 
  getAllMicroCredentials, 
  duplicateCriteria, 
  getCriteriaForCredential,
  duplicateRoles,
  getRolesForCredential,
  duplicateTeams,
  getTeamsForCredential
} from '@/api/admin';

export default {
  name: 'DataDuplication',
  components: {
    BaseButton,
    BaseInfoIcon,
  },
  data() {
    return {
      ERKLAERUNG,
      // Credentials
      credentials: [],
      loadingCredentials: false,
      
      // Form state
      selectedDataType: '',
      sourceCredentialId: null,
      selectedTargetIds: [],
      
      // Criteria
      availableCriteria: [],
      selectedCriteriaIds: [],
      selectAllCriteria: false,
      loadingCriteria: false,
      
      // Roles
      availableRoles: [],
      selectedRoleIds: [],
      selectAllRoles: false,
      loadingRoles: false,

      // Teams
      availableTeams: [],
      selectedTeamIds: [],
      selectAllTeams: false,
      loadingTeams: false,
      
      // Duplication state
      duplicating: false,
      duplicationResults: [],
      
      // Messages
      errorMessage: '',
      successMessage: '',
    };
  },
  computed: {
    targetCredentials() {
      if (this.sourceCredentialId === null) {
        return [];
      }
      return this.credentials.filter(c => c.id !== this.sourceCredentialId);
    },
    canDuplicate() {
      return (
        this.selectedDataType === 'criteria' &&
        this.sourceCredentialId !== null &&
        this.selectedTargetIds.length > 0
      );
    },
    canDuplicateRoles() {
      return (
        this.selectedDataType === 'roles' &&
        this.sourceCredentialId !== null &&
        this.selectedTargetIds.length > 0
      );
    },
    canDuplicateTeams() {
      return (
        this.selectedDataType === 'teams' &&
        this.sourceCredentialId !== null &&
        this.selectedTargetIds.length > 0
      );
    },
  },
  mounted() {
    this.loadCredentials();
  },
  methods: {
    async loadCredentials() {
      this.loadingCredentials = true;
      this.errorMessage = '';
      try {
        const response = await getAllMicroCredentials();
        this.credentials = response.data;
      } catch (error) {
        console.error('Error loading credentials:', error);
        this.errorMessage = 'Failed to load micro-credentials';
      } finally {
        this.loadingCredentials = false;
      }
    },
    
    onDataTypeChange() {
      // Reset form when data type changes
      this.sourceCredentialId = null;
      this.selectedTargetIds = [];
      this.availableCriteria = [];
      this.selectedCriteriaIds = [];
      this.selectAllCriteria = false;
      this.availableRoles = [];
      this.selectedRoleIds = [];
      this.selectAllRoles = false;
      this.availableTeams = [];
      this.selectedTeamIds = [];
      this.selectAllTeams = false;
      this.duplicationResults = [];
      this.errorMessage = '';
      this.successMessage = '';
    },
    
    async onSourceChange() {
      this.selectedTargetIds = [];
      this.availableCriteria = [];
      this.selectedCriteriaIds = [];
      this.selectAllCriteria = false;
      this.availableRoles = [];
      this.selectedRoleIds = [];
      this.selectAllRoles = false;
      this.availableTeams = [];
      this.selectedTeamIds = [];
      this.selectAllTeams = false;
      this.duplicationResults = [];
      this.errorMessage = '';
      this.successMessage = '';
      
      if (this.sourceCredentialId !== null && this.selectedDataType === 'criteria') {
        await this.loadCriteria();
      } else if (this.sourceCredentialId !== null && this.selectedDataType === 'roles') {
        await this.loadRoles();
      } else if (this.sourceCredentialId !== null && this.selectedDataType === 'teams') {
        await this.loadTeams();
      }
    },
    
    async loadCriteria() {
      this.loadingCriteria = true;
      try {
        const response = await getCriteriaForCredential(this.sourceCredentialId);
        this.availableCriteria = response.data;
      } catch (error) {
        console.error('Error loading criteria:', error);
        this.errorMessage = 'Failed to load criteria';
      } finally {
        this.loadingCriteria = false;
      }
    },
    
    async loadRoles() {
      this.loadingRoles = true;
      try {
        const response = await getRolesForCredential(this.sourceCredentialId);
        this.availableRoles = response.data;
      } catch (error) {
        console.error('Error loading roles:', error);
        this.errorMessage = 'Failed to load roles';
      } finally {
        this.loadingRoles = false;
      }
    },

    async loadTeams() {
      this.loadingTeams = true;
      try {
        const response = await getTeamsForCredential(this.sourceCredentialId);
        this.availableTeams = response.data;
      } catch (error) {
        console.error('Error loading teams:', error);
        this.errorMessage = 'Failed to load teams';
      } finally {
        this.loadingTeams = false;
      }
    },
    
    toggleSelectAll() {
      if (this.selectAllCriteria) {
        this.selectedCriteriaIds = this.availableCriteria.map(c => c.id);
      } else {
        this.selectedCriteriaIds = [];
      }
    },
    
    toggleSelectAllRoles() {
      if (this.selectAllRoles) {
        this.selectedRoleIds = this.availableRoles.map(r => r.id);
      } else {
        this.selectedRoleIds = [];
      }
    },

    toggleSelectAllTeams() {
      if (this.selectAllTeams) {
        this.selectedTeamIds = this.availableTeams.map(team => team.id);
      } else {
        this.selectedTeamIds = [];
      }
    },
    
    async duplicateCriteriaData() {
      if (!this.canDuplicate) return;
      
      this.duplicating = true;
      this.errorMessage = '';
      this.successMessage = '';
      this.duplicationResults = [];
      
      try {
        // If no criteria selected, pass null to duplicate all
        const criteriaIds = this.selectedCriteriaIds.length > 0 
          ? this.selectedCriteriaIds 
          : null;
        
        const response = await duplicateCriteria(
          this.sourceCredentialId,
          this.selectedTargetIds,
          criteriaIds
        );
        
        this.duplicationResults = response.data.results;
        
        if (response.data.success) {
          this.successMessage = response.data.message;
          
          // Calculate totals
          const totalDuplicated = this.duplicationResults.reduce(
            (sum, r) => sum + r.duplicated_count, 
            0
          );
          const totalSkipped = this.duplicationResults.reduce(
            (sum, r) => sum + r.skipped_count, 
            0
          );
          
          if (totalDuplicated > 0) {
            this.successMessage += ` (${totalDuplicated} item(s) duplicated, ${totalSkipped} skipped)`;
          }
        } else {
          this.errorMessage = response.data.message;
        }
      } catch (error) {
        console.error('Error duplicating criteria:', error);
        this.errorMessage = error.response?.data?.detail || 'Failed to duplicate criteria';
      } finally {
        this.duplicating = false;
      }
    },
    
    async duplicateRolesData() {
      if (!this.canDuplicateRoles) return;
      
      this.duplicating = true;
      this.errorMessage = '';
      this.successMessage = '';
      this.duplicationResults = [];
      
      try {
        // If no roles selected, pass null to duplicate all
        const roleIds = this.selectedRoleIds.length > 0 
          ? this.selectedRoleIds 
          : null;
        
        const response = await duplicateRoles(
          this.sourceCredentialId,
          this.selectedTargetIds,
          roleIds
        );
        
        this.duplicationResults = response.data.results;
        
        if (response.data.success) {
          this.successMessage = response.data.message;
          
          // Calculate totals
          const totalDuplicated = this.duplicationResults.reduce(
            (sum, r) => sum + r.duplicated_count, 
            0
          );
          const totalSkipped = this.duplicationResults.reduce(
            (sum, r) => sum + r.skipped_count, 
            0
          );
          
          if (totalDuplicated > 0) {
            this.successMessage += ` (${totalDuplicated} item(s) duplicated, ${totalSkipped} skipped)`;
          }
        } else {
          this.errorMessage = response.data.message;
        }
      } catch (error) {
        console.error('Error duplicating roles:', error);
        this.errorMessage = error.response?.data?.detail || 'Failed to duplicate roles';
      } finally {
        this.duplicating = false;
      }
    },

    async duplicateTeamsData() {
      if (!this.canDuplicateTeams) return;

      this.duplicating = true;
      this.errorMessage = '';
      this.successMessage = '';
      this.duplicationResults = [];

      try {
        const teamIds = this.selectedTeamIds.length > 0 ? this.selectedTeamIds : null;
        const response = await duplicateTeams(
          this.sourceCredentialId,
          this.selectedTargetIds,
          teamIds
        );
        this.duplicationResults = response.data.results;

        if (response.data.success) {
          this.successMessage = response.data.message;
        } else {
          this.errorMessage = response.data.message;
        }
      } catch (error) {
        console.error('Error duplicating teams:', error);
        this.errorMessage = error.response?.data?.detail || 'Failed to duplicate teams';
      } finally {
        this.duplicating = false;
      }
    },
  },
};
</script>

<style scoped>
/* Add any custom styles here if needed */
</style>
